import React, { useState, useRef } from "react";
import { Tooltip, theme, Modal, Space, Badge, Button, Select } from "antd";
import {
  PlusCircleFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  FileSearchOutlined,
  CheckCircleFilled,
  MinusCircleFilled,
  RetweetOutlined,
  FilePdfOutlined,
  RedoOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import ColorButton from "../../common/ColorButton";
import { formatComma } from "../../frameworks/Util";
import TSTable from "../../common/TSTable";
import ErrorMessages from "../../common/ErrorMessages";
import { URL_REPORT, URL_SALE, URL_SKIM } from "../../constants/urls";
import ProduceLotSkimModal from "./ProduceLotSkimModal";
import { DELETE, GET, POST } from "../../frameworks/HttpClient";
import CustomDeleteConfirmButton from "../../common/CustomDeleteConfirmButton";
import { PRIMARY_COLOR, PRINT_COLOR } from "../../constants/color";
import { PRODUCT_TYPE } from "../../constants/string";
import SkimWaitingOrderListModal from "./SkimWaitingOrderListModal";
import ReportButton from "../common/ReportButton";
import SkimProduceChangePOModal from "./SkimProduceChangePOModal";
import { PATH } from "../../routes/CustomRoutes";
import _ from "lodash";
import ProduceSkimLogModal from "./ProduceSkimLogModal";


export default function ProduceLotSkimView() {
  const tableRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [ errorMessages, setErrorMessages] = useState(null);
  const { token: { colorWarning, colorSuccess }, } = theme.useToken();
  const [productOrderCount, setProduceOrderCount] = useState(0);
  const [openProduceOrderListModal, setOpenProduceOrderListModal] = useState(null);
  const [changePOTarget, setChangePOTarget] = useState(null)
  const [sourceTarget, setSourceTarget] = useState(null)
  const [filterProductAmount, setFilterProductAmount] = useState(true)

  const fetchProduceOrderCount = async() => {
    try{
      const filter = { page_size: 999, type: PRODUCT_TYPE.SKIM, is_produced : false }
      const response = await GET(URL_SALE.SKIM_PRODUCE_ORDER, filter)
      setProduceOrderCount(response.data.total)
    } catch(error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const refreshData = async () => {
    if (tableRef.current) {
      setErrorMessages(null);
      try {
        await tableRef.current.fetchWithoutResetPage();
        fetchProduceOrderCount();
      } catch (error) { 
        setErrorMessages(error.errorMessages);
      }
    }
  };

  const refreshDataFilter = async (params) => {
    if (tableRef.current) {
      setErrorMessages(null);
      try {
        await tableRef.current.fetch(params);
        fetchProduceOrderCount();
      } catch (error) { 
        setErrorMessages(error.errorMessages);
      }
    }
  };

  const confirmEnd = (title, value) => {
    Modal.confirm({
      title: `ยืนยันการลงคลังครบจำนวน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'ล็อตการผลิตนี้ลงคลังสินค้าครบจำนวนแล้วใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleEndWork(value)},
      onConfirm: () => refreshData()
    });
  };

  const confirmRevert = (title, value) => {
    Modal.confirm({
      title: `ย้อนกลับสถานะ - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : PRIMARY_COLOR}} />,
      content: 'ต้องการย้อนสถานะใช่หรือไม่ ?',
      okText: 'ยืนยัน',
      cancelText: 'ปิด',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : PRIMARY_COLOR}},
      onOk:() => {handleRevert(value)},
      onConfirm: () => refreshData()
    });
  };

  const handleRevert = async(id) => {
    try {
      await POST(`${URL_SKIM.SKIM_PRODUCE_TRANSACTION}${id}/revert/`)
      refreshData();
    } catch (error) {
      setErrorMessages(null)
    }
  }

  const handleEndWork = async(id) => {
    try {
      await POST(`${URL_SKIM.SKIM_PRODUCE_TRANSACTION}${id}/complete/`)
      refreshData();
    } catch (error) {
      setErrorMessages(null)
    }
  }

  const handleDelete = async(id) => {
    try {
      await DELETE(`${URL_SKIM.SKIM_PRODUCE_TRANSACTION}${id}/`);
      refreshData();
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const columns = [
    {
      title: "Action",
      dataIndex: "",
      width: 150,
      key: "action",
      align: "center",
      render: (_, original) =>
        (
          <div onClick={(e) => e.stopPropagation()}>
            {!original.is_completed && <Tooltip triger='hover' title="ยืนยันการลงคลังครบจำนวน"> 
              <ColorButton
                type="primary"
                override={colorSuccess}
                icon={<CheckOutlined />}
                style={{ margin : 4 }}
                onClick={() => {
                  confirmEnd(original.lot_no, original.id)
                }}
              />
            </Tooltip>}
            {!original.is_completed && <Tooltip triger='hover' title="แก้ไข"> 
              <ColorButton
                type="primary"
                override={colorWarning}
                icon={<EditOutlined />}
                style={{ margin : 4 }}
                onClick={() => {
                  setTarget(original);
                }}
              />
            </Tooltip>}
            {original.deletable && <CustomDeleteConfirmButton
              title={`ลบล็อตการผลิต`}
              content={"คุณต้องการลบล็อตการผลิตใช่หรือไม่"}
              onOk={ async() => {
                await handleDelete(original.id)
                refreshData();
              }}
              onConfirm={() => refreshData()}
            />}
            { original.is_completed && (
              <Tooltip target='hover' title='เปลี่ยนใบแจ้งผลิต'>
                <ColorButton
                  type='primary'
                  override={colorWarning}
                  style={{ margin : 4 }}
                  icon={<RetweetOutlined />}
                  onClick={() => setChangePOTarget(original)}/>
              </Tooltip>
            )}
            { original.is_completed && (
              <Tooltip target='hover' title='พิมพ์ใบจำหน่ายสกิม'>
                <ReportButton
                  style={{ margin : 4 }}
                  url={`${URL_REPORT.SKIM_DISPATCH}${original.id}`}/>
              </Tooltip>
            )}
            { original.is_completed && (
              <Tooltip target='hover' title='ย้อนสถานะ'>
                <ColorButton
                  type='primary'
                  style={{ margin : 4 }}
                  override={PRIMARY_COLOR}
                  icon={<RedoOutlined />}
                  onClick={() => confirmRevert(original.lot_no, original.id)}/>
              </Tooltip>
            )}
          </div>
        ),
    },
    {
      title: "ล็อตการผลิต",
      dataIndex: "lot_no",
      key: "lot_no",
      searcher : true,
      render : (value, original) => value ? 
      <>
        {value}
        <Tooltip title={"รายละเอียดล็อตการผลิตสกิม"}>
          <Button
            color='primary' 
            variant='link'
            icon={<FilePdfOutlined />}
            onClick={()=> window.open(`${window.origin}${PATH.SKIM_SUMMARY}/${original.id}/`)}
          />
        </Tooltip>
      </> : '-'
    },
    {
      title: "ล็อตใบแจ้งผลิต",
      dataIndex: "produce_lot_no",
      key: "produce_lot_no",
      searcher : true,
      render:(value) => value ? value : '-'
    },
    {
      title: "มีใบแจ้งผลิต",
      dataIndex: "order_ref",
      key: "order_ref",
      render:(value, original) => 
        <center> 
          {value ? 
            <Tooltip title={
              <div>
                Contract No. : {original.sale_order_no}<br/>
                ล็อตใบแจ้งผลิต : {original.produce_lot_no}
              </div>
            }>
              <CheckCircleFilled style={{color : colorSuccess, fontSize : 16}}/> 
            </Tooltip>
          : 
            <Tooltip title={'ยังไม่ได้ผูกกับใบแจ้งผลิต'}>
              <MinusCircleFilled style={{color : colorWarning, fontSize : 16}}/>
            </Tooltip>
          }
        </center> 
    },
    {
      title: "น้ำหนัก",
      dataIndex: "weight",
      key: "weight",
      align: "center",
      render: (value) => formatComma(value)
    },
    {
      title: "สินค้า",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "จำนวนทั้งหมด Load(ก้อน)",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: (value) => formatComma(value)
    },
    {
      title: "รอใส่พาเลท (ก้อน)",
      dataIndex: "current_amount",
      key: "current_amount",
      align: "center",
      render: (value) => formatComma(value)
    },
    {
      title: "จำนวนคงเหลือ (ก้อน)",
      dataIndex: "current_stock_amount",
      key: "current_stock_amount",
      align: "center",
      render: (value, original) => <center>{formatComma(value)}<Button color='primary' variant='link' onClick={() => setSourceTarget(original)} icon={<EyeOutlined/>}/></center>
    },
    {
      title: "สถานะ",
      dataIndex: "is_completed",
      key: "is_completed",
      searcher: true,
      searcherOptions: [
        { key: 'true', value: true, label: 'ลงคลังครบจำนวนแล้ว' },
        { key: 'false', value: false, label: 'กำลังลงคลัง' },
      ],
      render: (value) => value === true ? 'ลงคลังครบจำนวนแล้ว' : 'กำลังลงคลัง'
    },
  ];

  React.useEffect(() => {
    fetchProduceOrderCount(); 
  },[])

  return (
    <div>
      <ErrorMessages message={errorMessages}/>
      สถานะ : 
      <Select
        style={{ marginLeft : 10, width : 250, }}
        options={[
          {
            label : "มีสินค้า/กำลังดำเนินการ",
            value : true
          },
          {
            label : "สินค้าหมด",
            value : false
          },
          {
            label : "ทั้งหมด",
            value : ''
          },
        ]}
        value={filterProductAmount}
        onChange={(e) => {
          setFilterProductAmount(e)
          refreshDataFilter({ has_product_amount : e})
        }}
      >

      </Select>
      <ColorButton
        override={colorSuccess}
        type="primary"
        style={{ marginBottom: 10, float: "right"   }}
        icon={<PlusCircleFilled />}
        onClick={() => {
          setTarget('add')
        }}
      >
        เพิ่มล็อตการผลิต
      </ColorButton>
      <Space style={{ float : "right", marginBottom: 10, marginRight: 10, }}>
        <Badge count={productOrderCount}>
          <ColorButton
            type={"primary"}
            override={PRINT_COLOR}
            icon={<FileSearchOutlined />}
            onClick={() => {
              setOpenProduceOrderListModal(true);
            }}
          >
            รายการรอผลิต
          </ColorButton>
        </Badge>
      </Space>

      <TSTable
        columns={columns}
        size="small"
        url={URL_SKIM.SKIM_PRODUCE_TRANSACTION}
        ref={tableRef}
        params={{ has_product_amount : filterProductAmount}}
      />

      <ProduceLotSkimModal
        open={target ? true : false}
        onClose={() => {
          setTarget(null)
        }}
        onUpdate={() => {
          setTarget(null)
          refreshData();
        }}
        target={target}
      />

      <SkimWaitingOrderListModal
        open={openProduceOrderListModal}
        onClose={() => setOpenProduceOrderListModal(false)}
        onUpdate={() => 
          setOpenProduceOrderListModal(false)
        }
      />

      <SkimProduceChangePOModal
        open={changePOTarget != null}
        target={changePOTarget}
        onCancel={() => setChangePOTarget(null)}
        onSuccess={() => {
          setChangePOTarget(null)
          refreshData()
        }}/>
      
      <ProduceSkimLogModal
        open={sourceTarget ? true : false}
        target={sourceTarget}
        onClose={() => setSourceTarget(null)}
      />
    </div>
  );
}
